/***
**Module Name: stripeStatusMeta
 **File Name :  stripeStatusMeta.js
 **Project :    SPOT SAVER
 **Copyright(c): SPOT SAVER.
 **Organization: SPOT SAVER
 **author :     Hari
 **license :
 **version :    1.0.0
 **Created on : Sep 04 2026
 **Last modified on:
 **Description: The one place a client's Stripe Connect status is turned into words and
 **             colours. Shared by the pill in the Clients table and the panel on View
 **             Client, so the same account can never be described two different ways on
 **             two screens — which is the failure that makes an admin trust neither.
 **
 **             The four statuses come from the API (models/MongoDB/stripeConnect.js,
 **             summarizeStoredAccount) and mean exactly what they say there:
 **               NOT_CONNECTED — no Stripe account in the mode the platform is running.
 **               INCOMPLETE    — account exists, onboarding never finished.
 **               RESTRICTED    — details submitted, but Stripe is holding payouts.
 **               CONNECTED     — payouts enabled.
 **
 **             Every one of these is a CACHED answer, refreshed by Stripe's
 **             account.updated webhook rather than read live per row — hence
 **             statusUpdatedAt travelling alongside it. See the API module for why.
 ***/

// Same values as components/settings/StripeModeCard.jsx pulls from spot-admin.css's
// :root, so the Stripe surfaces across the admin sit in one palette.
export const HEADING = "#4F378A";
export const MUTED = "#5C6188";
export const BORDER = "#E2E8F0";
export const PANEL = "#FAFBFE";
export const DANGER = "#DC5354";
export const GREEN = "#0F9D58";
export const AMBER = "#C25D18";

const META = {
    CONNECTED: {
        // Labelled by what an admin can actually see happening — money landing in a
        // bank — rather than by Stripe's internal flag name.
        label: "Bank Connected",
        // When payouts are on but our cached bank summary is empty (a row that predates
        // the bank cache, or one Stripe has not re-sent yet), claiming a bank is on file
        // would be inventing a fact. The capability is still true, so say only that.
        labelWithoutBank: "Payouts Active",
        icon: "check_circle",
        color: GREEN,
        tint: "rgba(15, 157, 88, 0.09)",
        line: "rgba(15, 157, 88, 0.35)",
        blurb: "Payouts are active. Earnings settle to the client's bank on their schedule."
    },
    RESTRICTED: {
        label: "Payouts Held",
        icon: "error_outline",
        color: DANGER,
        tint: "rgba(220, 83, 84, 0.08)",
        line: "rgba(220, 83, 84, 0.32)",
        blurb: "The client finished onboarding, but Stripe is holding payouts until it gets what it is still asking for."
    },
    INCOMPLETE: {
        label: "Setup Unfinished",
        icon: "hourglass_top",
        color: AMBER,
        tint: "rgba(251, 139, 69, 0.09)",
        line: "rgba(251, 139, 69, 0.45)",
        blurb: "A Stripe account exists but onboarding was never completed, so nothing can be paid out yet."
    },
    NOT_CONNECTED: {
        label: "Not Connected",
        icon: "link_off",
        color: MUTED,
        tint: "rgba(92, 97, 136, 0.08)",
        line: BORDER,
        blurb: "No Stripe account in this mode. The client's paid plans stay hidden from the app until they onboard."
    }
};

/**
 * The presentation for one client's `stripe` block, or null when the block is absent.
 *
 * Null is not the same as NOT_CONNECTED and must not be rendered as it: the API and the
 * admin deploy separately, so a missing block means "this build of the API does not
 * report Stripe status", and painting that as "Not Connected" would tell an admin every
 * onboarded venue had lost its account.
 */
export const stripeStatusMeta = (stripe) => {
    if (!stripe || !stripe.status) return null;
    const meta = META[stripe.status];
    if (!meta) return null;
    return {
        ...meta,
        label: (stripe.status === "CONNECTED" && !stripe.bankConnected)
            ? meta.labelWithoutBank
            : meta.label
    };
};

/** "STRIPE TEST BANK ••2227", or "" when no bank is on file. */
export const formatBank = (stripe) => {
    if (!stripe || !stripe.bankLast4) return "";
    return `${stripe.bankName || "Bank account"} ••${stripe.bankLast4}`;
};

/**
 * The payout schedule in words.
 *
 * Reads the interval Stripe actually has on the account, and falls back to the schedule
 * an admin asked for only when Stripe has not told us one yet — an account that has
 * never been reached still shows the intent rather than a blank.
 */
export const formatSchedule = (stripe) => {
    if (!stripe) return "";
    const interval = stripe.scheduleInterval || stripe.payoutSchedule || "";
    if (!interval) return "";
    const cap = interval.charAt(0).toUpperCase() + interval.slice(1);
    if (interval === "weekly" && stripe.scheduleWeeklyAnchor) {
        const day = stripe.scheduleWeeklyAnchor;
        return `${cap} (${day.charAt(0).toUpperCase()}${day.slice(1)}s)`;
    }
    if (interval === "monthly" && stripe.scheduleMonthlyAnchor) {
        return `${cap} (day ${stripe.scheduleMonthlyAnchor})`;
    }
    if (interval === "daily" && stripe.scheduleDelayDays != null) {
        return `${cap} (${stripe.scheduleDelayDays}-day rolling delay)`;
    }
    return cap;
};

/**
 * Stripe's requirement codes are dotted machine names (individual.id_number). Spelled
 * out just enough to be readable while staying recognisable against Stripe's own
 * dashboard — nothing is renamed or interpreted, only spaced out.
 */
export const formatRequirement = (requirement) =>
    String(requirement || "").replace(/[._]/g, " ").trim();
